// Shared across documents. Canvas memory is counted as RGBA, not compressed bytes.
const surfaces = new Map();
let surfaceBytes = 0, serial = 0;
const MAX_BYTES = 32 * 1024 * 1024;
function remove(key) {
    const value = surfaces.get(key);
    if (!value) return;
    surfaceBytes -= value.width * value.height * 4;
    value.width = value.height = 0;
    surfaces.delete(key);
}
function remember(key, canvas) {
    surfaces.set(key, canvas);
    surfaceBytes += canvas.width * canvas.height * 4;
    while (surfaceBytes > MAX_BYTES) remove(surfaces.keys().next().value);
}
export function createPageRenderer(getPdf, makeCanvas = () => document.createElement('canvas')) {
    const prefix = `${++serial}:`, jobs = new Map();
    let active, disposed = false;
    const keyFor = (page, width) => `${prefix}${page}:${Math.ceil(width / 64) * 64}`;
    function get(page, width) {
        const key = keyFor(page, width), canvas = surfaces.get(key);
        if (canvas) { surfaces.delete(key); surfaces.set(key, canvas); }
        return canvas;
    }
    async function pump() {
        if (active || disposed) return;
        const job = [...jobs.values()].filter(j => !j.started).sort((a, b) => b.priority - a.priority)[0];
        if (!job) return;
        active = job; job.started = true;
        let canvas;
        try {
            const pdf = await getPdf();
            if (job.cancelled) throw new Error('Cancelled');
            const page = await pdf.getPage(job.page);
            job.pdfPage = page;
            if (job.cancelled) throw new Error('Cancelled');
            const base = page.getViewport({ scale: 1 });
            const scale = Math.min(job.width / base.width, Math.sqrt(2000000 / (base.width * base.height)));
            const viewport = page.getViewport({ scale });
            canvas = makeCanvas();
            canvas.width = Math.max(1, Math.ceil(viewport.width));
            canvas.height = Math.max(1, Math.ceil(viewport.height));
            job.task = page.render({ canvasContext: canvas.getContext('2d'), viewport, intent: 'print' });
            await job.task.promise;
            if (disposed || job.cancelled) throw new Error('Cancelled');
            remember(job.key, canvas);
            job.resolve(canvas);
        } catch (error) {
            if (canvas) canvas.width = canvas.height = 0;
            job.reject(error);
        } finally {
            try { job.pdfPage?.cleanup?.(); } catch { /* PDF may already be destroyed. */ }
            if (jobs.get(job.key) === job) jobs.delete(job.key);
            active = null;
            void pump();
        }
    }
    function cancel(job) {
        job.cancelled = true;
        job.task?.cancel();
        if (!job.started) { jobs.delete(job.key); job.reject(new Error('Cancelled')); }
    }
    // Any resolution of the page, largest first: shown instantly while the exact size renders.
    function best(page) {
        const start = `${prefix}${page}:`;
        let found;
        for (const [key, canvas] of surfaces) if (key.startsWith(start) && (!found || canvas.width > found.width)) found = canvas;
        return found;
    }
    return {
        get,
        best,
        load(page, width, priority = 1) {
            if (disposed) return Promise.reject(new Error('Preview closed'));
            const cached = get(page, width);
            if (cached) return Promise.resolve(cached);
            const key = keyFor(page, width);
            let job = jobs.get(key);
            if (job?.cancelled) job = null;
            if (!job) {
                job = { key, page, width: Math.ceil(width / 64) * 64, priority };
                job.promise = new Promise((resolve, reject) => { job.resolve = resolve; job.reject = reject; });
                jobs.set(key, job);
            }
            job.priority = Math.max(job.priority, priority);
            if (priority) {
                for (const other of jobs.values()) if (other !== job && other.priority) cancel(other);
                if (active && active !== job) cancel(active);
            }
            void pump();
            return job.promise;
        },
        pause() { for (const job of jobs.values()) cancel(job); },
        dispose() {
            disposed = true;
            for (const job of jobs.values()) cancel(job);
            for (const key of surfaces.keys()) if (key.startsWith(prefix)) remove(key);
        },
    };
}
